/**
 * Login Page
 *
 * Handles user authentication via Supabase.
 * Redirects to dashboard on successful login.
 */

import {
  Alert,
  AlertDescription,
  AuthTemplate,
  Button,
  DividerWithText,
  Form,
  Grid,
  Inline,
  Input,
  Label,
  SocialLoginButton,
  Stack,
  Text,
} from '@cortex/ui';
import {
  createFileRoute,
  Link,
  redirect,
  useSearch,
} from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import { supabase } from '@/lib/supabase';

// Search params validation
const loginSearchSchema = z.object({
  redirect: z.string().optional(),
  message: z.string().optional(),
});

export const Route = createFileRoute('/login')({
  validateSearch: loginSearchSchema,
  beforeLoad: ({ context }) => {
    // If already authenticated, redirect to app
    if (context.auth.isAuthenticated && !context.auth.isLoading) {
      throw redirect({ to: '/' });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const search = useSearch({ from: '/login' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        setError(signInError.message);
        return;
      }

      // Redirect to the original destination or dashboard
      const redirectTo = search.redirect ?? '/';
      // Use window.location for redirects to handle routes not yet in router
      window.location.href = redirectTo;
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (signInError) {
      setError(signInError.message);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        scopes: 'email',
      },
    });

    if (signInError) {
      setError(signInError.message);
    }
  };

  return (
    <AuthTemplate
      title="Sign in to Cortex"
      subtitle="AI-powered digital delivery platform"
    >
      {search.message && (
        <Alert variant="success">
          <AlertDescription>{search.message}</AlertDescription>
        </Alert>
      )}

      <Form onSubmit={handleSubmit} className="mt-8" gap="lg">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Stack gap="md">
          <Stack gap="xs">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </Stack>

          <Stack gap="xs">
            <Inline justify="between">
              <Label htmlFor="password">Password</Label>
              <Link
                to="/forgot-password"
                className="text-primary text-sm hover:underline"
              >
                Forgot password?
              </Link>
            </Inline>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </Stack>
        </Stack>

        <Button type="submit" loading={isLoading} className="w-full">
          Sign in
        </Button>

        <DividerWithText text="Or continue with" />

        <Grid columns={2} gap="md">
          <SocialLoginButton
            provider="google"
            variant="outline"
            onClick={handleGoogleLogin}
            disabled={isLoading}
          >
            Google
          </SocialLoginButton>
          <SocialLoginButton
            provider="microsoft"
            variant="outline"
            onClick={handleMicrosoftLogin}
            disabled={isLoading}
          >
            Microsoft
          </SocialLoginButton>
        </Grid>
      </Form>

      <Text color="secondary" align="center" size="sm">
        Don&apos;t have an account?{' '}
        <Link
          to="/signup"
          search={{ redirect: search.redirect }}
          className="text-primary hover:underline"
        >
          Sign up
        </Link>
      </Text>
    </AuthTemplate>
  );
}
